import type { AxiosRequestConfig } from 'axios';
import { Http } from '@mygpt/app/http';

// bot 級「回答ルール」記憶（設計：felo-mygpt docs/knowledge_loop/bot_reply_memory_design.md）。
// bot ごとの opt-in、既定 OFF。OFF の bot はドラフト生成に何も注入されない
const SILENT = { skipErrorNotification: true } as AxiosRequestConfig;

export type ReplyRuleKind = 'style_rule' | 'guard_rule' | 'phrase';
export type ReplyRuleStatus = 'active' | 'proposed' | 'archived';
export type ReplyRuleOrigin = 'auto' | 'manual' | 'chat';

export interface ReplyRule {
  id: string;
  kind: ReplyRuleKind;
  text: string;
  status: ReplyRuleStatus;
  origin: ReplyRuleOrigin;
  pinned: boolean;
  strength: number;
  hits: number;
  as_is_hits: number;
  evidence?: { draft_id?: string; ticket_id?: number; why?: string; weakened?: string }[] | null;
  created_by?: string | null;
  last_used_at?: string | null;
  last_reinforced_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ReplyRuleCounts {
  status: Record<ReplyRuleStatus, number>;
  kind: Record<ReplyRuleKind, number>;
}

export interface ReplyRuleProposal {
  kind: ReplyRuleKind;
  text: string;
  why?: string;
}

export const REPLY_RULE_KINDS: ReplyRuleKind[] = ['style_rule', 'guard_rule', 'phrase'];

export const getReplyRuleSettings = (robotId: string) => {
  const { get } = Http.use();
  return get<{ enabled: boolean }>(`/robots/${robotId}/reply-rules/settings`, SILENT);
};

export const putReplyRuleSettings = (robotId: string, enabled: boolean) => {
  const { put } = Http.use();
  return put<{ enabled: boolean }>(`/robots/${robotId}/reply-rules/settings`, { enabled }, SILENT);
};

export const listReplyRules = (robotId: string, status: ReplyRuleStatus) => {
  const { get } = Http.use();
  return get<{ enabled: boolean; items: ReplyRule[]; counts: ReplyRuleCounts }>(`/robots/${robotId}/reply-rules`, {
    params: { status },
    ...SILENT,
  });
};

export const createReplyRule = (robotId: string, body: { kind: ReplyRuleKind; text: string }) => {
  const { post } = Http.use();
  return post<ReplyRule>(`/robots/${robotId}/reply-rules`, body, SILENT);
};

export const patchReplyRule = (
  robotId: string,
  ruleId: string,
  body: { kind?: ReplyRuleKind; text?: string; status?: 'active' | 'archived' }
) => {
  const { patch } = Http.use();
  return patch<ReplyRule>(`/robots/${robotId}/reply-rules/${ruleId}`, body, SILENT);
};

/** 回答改善キューのドラフト差分から AI がルールを提案（保存しない） */
export const proposeReplyRules = (robotId: string, draftId: string) => {
  const { post } = Http.use();
  return post<{ proposals: ReplyRuleProposal[] }>(
    `/robots/${robotId}/zendesk/drafts/${draftId}/rule-proposals`,
    null,
    SILENT
  );
};
